import React from 'react';
import { Modal, cn } from '../ui/Core';
import { CloseIcon } from '@/assets/icons';

interface FormModalRootProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title: string;
    subtitle?: any;
    className?: string;
    maxWidth?: string;
    hideHeader?: boolean;
}

const FormModalHeader: React.FC<{ title: string; subtitle?: any; onClose: () => void; className?: string }> = ({ title, subtitle, onClose, className }) => (
    <div className={cn("flex items-start justify-between gap-6 px-7 py-5 border-b border-gray-100 bg-white z-10 flex-shrink-0", className)}>
        <div>
            <h2 className="text-xl font-semibold text-darkGreen font-outfit tracking-tight">
                {title}
            </h2>
            {subtitle && (
                <p className="text-xs text-gray-500 font-medium mt-1">
                    {subtitle}
                </p>
            )}
        </div>
        <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-darkGreen hover:bg-gray-100 rounded-lg transition-all duration-200 cursor-pointer"
            aria-label="Close"
        >
            <CloseIcon className="w-5 h-5" />
        </button>
    </div>
);

const FormModalRoot: React.FC<FormModalRootProps & { withoutModal?: boolean }> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    className,
    maxWidth = 'max-w-4xl',
    hideHeader = false,
    withoutModal = false,
}) => {
    const content = (
        <div className={cn("flex flex-col bg-white relative overflow-hidden", withoutModal ? `h-full ${ className }` : "")}>
            {!hideHeader && (
                <FormModalHeader title={title} subtitle={subtitle} onClose={onClose} />
            )}
            {children}
        </div>
    );

    if (withoutModal) {
        return content;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            hideHeader={true}
            variant="premium"
            className={cn(maxWidth, "w-full", className)}
        >
            {content}
        </Modal>
    );
};

interface FormModalContentProps {
    children: React.ReactNode;
    className?: string;
}

const FormModalContent: React.FC<FormModalContentProps> = ({ children, className }) => (
    <div className={cn("flex-grow overflow-y-auto scroll-smooth relative z-0", className)}>
        {children}
    </div>
);

interface FormModalFooterProps {
    onCancel: () => void;
    onBack?: () => void;
    onNext?: () => void;
    onSubmit?: () => void;
    isFirstStep?: boolean;
    isLastStep?: boolean;
    isSubmitting?: boolean;
    canNext?: boolean;
    nextLabel?: string;
    submitLabel?: string;
    backLabel?: string;
    cancelLabel?: string;
    className?: string;
    children?: React.ReactNode;
    hideCancel?: boolean;
    hideBack?: boolean;
    hideSubmit?: boolean;
    disabled?: boolean;
    submitVariant?: 'primary' | 'danger' | 'secondary';
}

const FormModalFooter: React.FC<FormModalFooterProps> = ({
    onCancel,
    onBack,
    onNext,
    onSubmit,
    isFirstStep = true,
    isLastStep = true,
    isSubmitting = false,
    canNext = true,
    nextLabel = 'Continue to Next Step',
    submitLabel = 'Finish & Create',
    backLabel = 'Go Back',
    cancelLabel = 'Cancel',
    className,
    children,
    hideCancel = false,
    hideBack = false,
    hideSubmit = false,
    disabled = false,
    submitVariant = 'primary',
}) => (
    <div className={cn("sticky bottom-0 flex items-center justify-between px-7 py-5 border-t border-gray-100 bg-white/95 backdrop-blur-sm z-10 flex-shrink-0", className)}>
        {children}
        <div className={cn("flex items-center space-x-4", !children && "w-full justify-between")}>
            {!children && !hideBack && (
                <button
                    onClick={onBack}
                    disabled={isFirstStep}
                    className="px-6 py-3 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 cursor-pointer"
                >
                    {backLabel}
                </button>
            )}
            <div className="flex items-center space-x-4 ml-auto">
                {!hideCancel && (
                    <button
                        onClick={onCancel}
                        className="px-5 py-3 text-xs font-semibold text-gray-400 hover:text-darkGreen hover:bg-gray-50 rounded-lg transition-all duration-200 cursor-pointer"
                    >
                        {cancelLabel}
                    </button>
                )}
                {!hideSubmit && (
                    <button
                        onClick={isLastStep ? onSubmit : onNext}
                        disabled={!canNext || isSubmitting || disabled}
                        className={cn(
                            "px-8 py-3 text-xs font-semibold text-white border border-transparent rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center gap-3 cursor-pointer",
                            submitVariant === 'danger' ? "bg-red-500 hover:bg-red-600" :
                                submitVariant === 'secondary' ? "bg-slate-500 hover:bg-slate-600" :
                                    "bg-primaryGreen hover:opacity-90",
                            "active:scale-[0.98]"
                        )}
                    >
                        <span>{isSubmitting ? 'Processing...' : (isLastStep ? submitLabel : nextLabel)}</span>
                        {!isLastStep && !isSubmitting && (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        )}
                    </button>
                )}
            </div>
        </div>
    </div>
);

interface FormModalStepProgressProps {
    currentStep: number;
    totalSteps: number;
    stepLabels?: string[];
    progress: number;
    caption?: string;
}

const FormModalStepProgress: React.FC<FormModalStepProgressProps> = ({
    currentStep,
    totalSteps,
    stepLabels,
    progress,
    caption = 'Progress Optimization',
}) => (
    <div className="px-8 pt-6 pb-2 bg-white relative z-10 flex-shrink-0">
        <div className="flex justify-between items-end mb-3">
            <div className="flex flex-col">
                <span className="text-[10px] font-bold text-gray-400 tracking-[0.2em] uppercase mb-0.5">
                    {caption}
                </span>
                <span className="text-sm font-bold text-primaryGreen tracking-tight">
                    STEP {currentStep} OF {totalSteps}{stepLabels?.[currentStep - 1] ? `: ${ stepLabels[currentStep - 1] }` : ''}
                </span>
            </div>
            <span className="text-xs font-bold text-darkGreen tracking-tighter">{Math.round(progress)}% Complete</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
                className="bg-primaryGreen h-1.5 rounded-full transition-all duration-500 ease-out shadow-[0_0_8px_var(--app-effect22x163x74a0_4)]"
                style={{ width: `${ progress }%` }}
            ></div>
        </div>
    </div>
);

export const FormModal = Object.assign(FormModalRoot, {
    Content: FormModalContent,
    Footer: FormModalFooter,
    Header: FormModalHeader,
    StepProgress: FormModalStepProgress,
});
